import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Printer, Loader2 } from "lucide-react";
import { inr, fdate } from "@/components/FeeLedgerDialog";

export function InvoiceDialog({ planId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (planId) { setD(null); api.get(`/fee-mgmt/plans/${planId}/invoice`).then((r) => setD(r.data)).catch((e) => { toast.error(formatApiError(e)); onClose(); }); }
  }, [planId]); // eslint-disable-line react-hooks/exhaustive-deps
  function print() {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${d.invoice_no}</title><style>body{font-family:system-ui,sans-serif;padding:32px;color:#0f172a}table{width:100%;border-collapse:collapse}td,th{padding:8px 10px;border-bottom:1px solid #e2e8f0;font-size:13px}th{text-align:left;background:#f5f3ff}.r{text-align:right}.neg{color:#be123c}.tot td{font-weight:700;font-size:15px;background:#f5f3ff}.muted{color:#64748b;font-size:11px}</style></head><body>${document.getElementById("invoice-print").innerHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  }
  const p = d?.plan;
  return (
    <Dialog open={!!planId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl">Fee invoice</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="animate-spin text-violet-500" /> : (<>
          <div id="invoice-print" data-testid="invoice-body">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <div><div className="font-display font-extrabold text-lg">{d.branch.name}</div><div className="muted text-xs text-slate-500">{d.branch.location}</div></div>
              <div style={{ textAlign: "right" }}><div className="font-semibold">{d.invoice_no}</div><div className="muted text-xs text-slate-500">{fdate(new Date().toISOString())}</div></div>
            </div>
            <div className="text-sm mb-3"><b>{p.student_name}</b> • {p.admission_number} • {p.class_name}{p.section ? ` ${p.section}` : ""} • {p.academic_year}
              <div className="muted text-xs text-slate-500">Parent: {d.student.father_name || d.student.parent_name || "—"} {d.student.parent_phone ? `• ${d.student.parent_phone}` : ""} • Kit choice: {d.kit_option_label}</div></div>
            <table className="w-full text-sm">
              <thead><tr className="bg-violet-50"><th className="text-left px-3 py-2">Item</th><th className="r text-right px-3 py-2">Amount</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {d.items.map((i) => (
                  <tr key={i.label} data-testid={`invoice-line-${i.label.toLowerCase().replace(/\W+/g, "-")}`}>
                    <td className="px-3 py-2">{i.label}{i.note && <span className="muted text-xs text-slate-400"> — {i.note}</span>}</td>
                    <td className="r text-right px-3 py-2 font-medium">{inr(i.amount)}</td>
                  </tr>
                ))}
                <tr><td className="px-3 py-2 font-semibold">Subtotal</td><td className="r text-right px-3 py-2 font-semibold" data-testid="invoice-subtotal">{inr(d.subtotal)}</td></tr>
                {d.deductions.map((x) => (
                  <tr key={x.label} data-testid={`invoice-deduction-${x.label.toLowerCase().replace(/\W+/g, "-")}`}>
                    <td className="px-3 py-2 text-rose-700">{x.label}</td><td className="r neg text-right px-3 py-2 text-rose-700 font-medium">−{inr(x.amount)}</td>
                  </tr>
                ))}
                <tr className="tot bg-violet-50"><td className="px-3 py-2.5 font-bold">Total</td><td className="r text-right px-3 py-2.5 font-display font-extrabold text-violet-800" data-testid="invoice-total">{inr(d.total)}</td></tr>
                <tr><td className="px-3 py-2 text-emerald-700">Paid</td><td className="r text-right px-3 py-2 text-emerald-700">{inr(p.paid)}</td></tr>
                <tr><td className="px-3 py-2 font-semibold">Balance due</td><td className="r text-right px-3 py-2 font-semibold text-rose-700" data-testid="invoice-balance">{inr(p.pending)}</td></tr>
              </tbody>
            </table>
          </div>
          <Button data-testid="invoice-print-btn" onClick={print} variant="outline" className="w-full"><Printer size={15} className="mr-2" />Print / Save as PDF</Button>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
